/**
 * VIEW_PHOTO task template - emphasizes the photo to view
 *
 * @module pages/tasks/components/templates/ViewPhotoTaskTemplate
 */

import { Text } from '@/components/atoms/Text';
import { TaskDebugPanel } from '@/components/composed/TaskDebugPanel';
import { TargetPreview } from '../previews';
import { TaskActions } from './TaskActions';
import { getStatusColor } from '../../utils';
import type { TaskTemplateProps } from '../../types';

export function ViewPhotoTaskTemplate({ task, onNavigate, onComplete, onDismiss, onDelete, isUpdating, debugMode }: TaskTemplateProps) {
  return (
    <div className="bg-surface-1 border border-border rounded-lg p-4 hover:border-primary/50 transition-colors">
      <div className="flex items-start justify-between gap-4">
        {/* Main Content */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            <span className="text-sm font-medium text-foreground">View Photo</span>
            <span className={`px-2 py-0.5 text-xs font-medium rounded ${getStatusColor(task.status)}`}>
              {task.status}
            </span>
          </div>

          {/* Description if available */}
          {task.description && (
            <Text className="text-sm mb-3 line-clamp-2">{task.description}</Text>
          )}

          {/* Target Preview - prominent for view tasks */}
          <div className="mb-3">
            <TargetPreview task={task} />
          </div>

          {/* Metadata */}
          <div className="text-xs text-muted">
            <span>{new Date(task.createdAt).toLocaleTimeString()}</span>
          </div>

          {/* Debug Panel */}
          {debugMode && <TaskDebugPanel task={task} />}
        </div>

        {/* Actions */}
        <TaskActions
          task={task}
          onNavigate={onNavigate}
          onComplete={onComplete}
          onDismiss={onDismiss}
          onDelete={onDelete}
          isUpdating={isUpdating}
        />
      </div>
    </div>
  );
}
